// @dynamic-i18n-keys
//   saleBanner.preamble
//   saleBanner.sale
//   saleBanner.button
//   saleBanner.mobile.preamble
//   saleBanner.mobile.sale
//   saleBanner.mobile.button
//   saleBanner.narrow.preamble
//   saleBanner.narrow.sale
//   saleBanner.narrow.button
//   saleBanner.gift.preamble
//   saleBanner.gift.sale
//   saleBanner.gift.button
//   saleBanner.gift.mobile.preamble
//   saleBanner.gift.mobile.sale
//   saleBanner.gift.mobile.button
//   saleBanner.gift.narrow.preamble
//   saleBanner.gift.narrow.sale
//   saleBanner.gift.narrow.button
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { ThemeOverride, ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import GiftIcon from '@wix/da-ds/pkg/Icons/24x24/Gift';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import { BannerButton, BannerLayout, BannerMessage } from '@wix/da-ds/pkg/Banner/lego';
import s from './SaleBanner.scss';

export interface Props {
  discountValuePercent?: string;
  endDate?: string;
  giftRecipient?: string;
  isNarrow?: boolean;
  referralType?: ReferralType;
  className?: string;
}

const SaleBanner: React.FC<Props> = ({
  discountValuePercent,
  endDate,
  isNarrow,
  giftRecipient,
  referralType,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const isGift = !!giftRecipient;
  const localeKeys = getLocaleKeys(isGift, isMobile, isNarrow);
  const isShowingPreamble = !isNarrow && !isMobile;
  const href = giftRecipient ? Url.giveCoreMembership(giftRecipient) : URLS.CORE_MEMBERSHIP;

  if (!isLoggedIn) {
    return null;
  }

  return (
    <ThemeOverride className={s['theme-override-wrapper']} theme={ThemeOptions.LIGHT}>
      <BannerLayout
        className={c(s['root'], className)}
        size={isNarrow ? 'small' : 'big'}
        body={[
          isShowingPreamble && (
            <BannerMessage key="message-before-icon" weight="reg">
              {t(localeKeys.preamble, {
                username: giftRecipient,
              })}
            </BannerMessage>
          ),
          isGift ? (
            <GiftIcon
              key="gift-icon"
              className={s['gift-icon']}
              size={IconSize.SMALLER}
              role="presentation"
            />
          ) : (
            <CoreIcon key="core-icon" size={IconSize.SMALLER} role="presentation" />
          ),
          <BannerMessage key="message-after-icon" weight={isNarrow ? 'reg' : 'bld'}>
            {t(localeKeys.sale, {
              sale: discountValuePercent,
              date: endDate
                ? formatDate(endDate, DATE_FORMATS.Date.DayLongMonth, i18n.language)
                : undefined,
            })}
          </BannerMessage>,
        ]}
        postfix={
          <BannerButton
            variant={isNarrow ? 'underline' : 'ghost'}
            href={href}
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: referralType as any,
            })}
          >
            {t(localeKeys.button)}
          </BannerButton>
        }
      />{' '}
    </ThemeOverride>
  );
};
SaleBanner.displayName = 'SaleBanner';

export default SaleBanner;

interface LocaleKeys {
  preamble: string;
  sale: string;
  button: string;
}

function getLocaleKeys(isGift?: boolean, isMobile?: boolean, isNarrow?: boolean): LocaleKeys {
  // saleBanner.mobile.preamble
  // saleBanner.mobile.sale
  // saleBanner.mobile.button
  // saleBanner.narrow.preamble
  // saleBanner.narrow.sale
  // saleBanner.narrow.button
  // saleBanner.preamble
  // saleBanner.sale
  // saleBanner.button
  // saleBanner.gift.mobile.preamble
  // saleBanner.gift.mobile.sale
  // saleBanner.gift.mobile.button
  // saleBanner.gift.narrow.button
  // saleBanner.gift.preamble
  // saleBanner.gift.sale
  // saleBanner.gift.button
  let keyBase = 'saleBanner';
  if (isGift) {
    keyBase += '.gift';
  }
  if (isMobile) {
    keyBase += '.mobile';
  } else if (isNarrow) {
    keyBase += '.narrow';
  }

  return {
    preamble: `${keyBase}.preamble`,
    sale: `${keyBase}.sale`,
    button: `${keyBase}.button`,
  };
}
